import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { ensureSocialLoginInitialized } from './socialLoginInit';

const appleServiceId = process.env.REACT_APP_APPLE_SERVICE_ID || '';
const appleRedirectUri =
  process.env.REACT_APP_APPLE_REDIRECT_URI ||
  (typeof window !== 'undefined' ? window.location.origin : '');
const APPLE_JS_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

let appleJsPromise;

function isNativeIos() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
}

export function canUseAppleSignIn() {
  if (isNativeIos()) return true;
  if (Capacitor.isNativePlatform()) return false;
  return Boolean(appleServiceId);
}

function randomNonce() {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function formatName(givenName, familyName) {
  const name = [givenName, familyName].filter(Boolean).join(' ').trim();
  return name || null;
}

async function signInWithAppleNative(nonce) {
  await ensureSocialLoginInitialized();
  const res = await SocialLogin.login({
    provider: 'apple',
    options: { scopes: ['email', 'name'], nonce },
  });
  if (res.provider !== 'apple') {
    throw new Error('Unexpected login provider');
  }
  const { idToken, profile } = res.result || {};
  if (!idToken) {
    throw new Error('Apple did not return an id token');
  }
  return {
    idToken,
    nonce,
    user: {
      name: formatName(profile?.givenName, profile?.familyName),
      email: profile?.email || null,
    },
  };
}

function loadAppleJs() {
  if (window.AppleID?.auth) return Promise.resolve();
  if (!appleJsPromise) {
    appleJsPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = APPLE_JS_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        appleJsPromise = undefined;
        reject(new Error('Could not load Sign in with Apple'));
      };
      document.head.appendChild(script);
    });
  }
  return appleJsPromise;
}

async function signInWithAppleWeb(nonce) {
  await loadAppleJs();
  window.AppleID.auth.init({
    clientId: appleServiceId,
    scope: 'name email',
    redirectURI: appleRedirectUri,
    usePopup: true,
    nonce,
  });
  const data = await window.AppleID.auth.signIn();
  const idToken = data?.authorization?.id_token;
  if (!idToken) {
    throw new Error('Apple did not return an id token');
  }
  const name = data?.user?.name;
  return {
    idToken,
    nonce,
    user: {
      name: formatName(name?.firstName, name?.lastName),
      email: data?.user?.email || null,
    },
  };
}

/** Returns true when the error means the user dismissed the Apple sheet/popup. */
export function isAppleSignInCancelled(err) {
  const raw = err?.error || err?.code || err?.message || '';
  const text = String(raw).toLowerCase();
  return (
    text.includes('popup_closed_by_user') ||
    text.includes('user_cancelled_authorize') ||
    text.includes('cancel') ||
    text.includes('1001')
  );
}

export async function signInWithApple() {
  if (!canUseAppleSignIn()) {
    throw new Error('Sign in with Apple is not available on this device');
  }
  const nonce = randomNonce();
  return isNativeIos() ? signInWithAppleNative(nonce) : signInWithAppleWeb(nonce);
}

export async function signOutNativeApple() {
  if (!isNativeIos()) return;
  try {
    await ensureSocialLoginInitialized();
    await SocialLogin.logout({ provider: 'apple' });
  } catch {
    /* ignore */
  }
}
